import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import RedirectingScreen from "@/components/loading/RedirectingScreen";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { useInstallAppBanner } from "@/contexts";
import { useAgentPageByUrlNameFull } from "@/hooks/agent/useAgentPage";
import { useFilteredResources } from "@/hooks/agent/useFilteredResources";
import { useSuggestedResourceMessages } from "@/hooks/agent/useSuggestedResourceMessages";
import { usePublicHelpHub } from "@/hooks/help-hub/useHelpHub";
import { useRole } from "@/hooks/auth";
import { useInConversationCapture } from "@/hooks/voice/useInConversationCapture";
import { useNoiseEnvironment } from "@/hooks/voice/useNoiseEnvironment";
import { useCustomerMemoryConsent } from "@/hooks/voice/useCustomerMemoryConsent";
import { useVoiceTroubleSignals } from "@/hooks/voice/useVoiceTroubleSignals";
import { hasInChatMedia } from "@/lib/agent/mediaSources";
import { trackEventOnce } from "@/lib/ga4";
import { setLeadConversionSettings } from "@/lib/leadConversions";
import { useChannelRequests } from "@/hooks/voice/useChannelRequests";
import { useConversationFollowUp } from "@/hooks/voice/useConversationFollowUp";
import { useFoundMedia } from "@/hooks/voice/useFoundMedia";
import { useLatestAttachments } from "@/hooks/voice/useLatestAttachments";
import { useMediaLookupPending } from "@/hooks/voice/useMediaLookupPending";
import GenieInvitePage from "@/pages/agent-interfaces/GenieInvitePage";
import GenieAccessRestrictedPage from "@/pages/Errors/GenieAccessRestrictedPage";
import GenieNotFound from "@/pages/Errors/GenieNotFound";
import { AgentConfig } from "@/types/agent/agent";
import { AgentPageProps } from "@/types/agent/agent-page";
import { InfoLinksConfig } from "@/types/agent/info-links";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import EmbedLiveAgentExperience from "@/pages/agent-interfaces/embed/EmbedLiveAgentExperience";
import { hasAskAlreadyBeenSent, markAskAsSent, parseAskParam } from "@/pages/agent-interfaces/live-agent/askParam";
import { ListenerExperience } from "@/components/recorder/ListenerExperience";
import { InlineCaptureMoment } from "@/pages/agent-interfaces/live-agent/components/InlineCaptureMoment";
import { ConversationAlternativePrompt } from "@/pages/agent-interfaces/live-agent/components/ConversationAlternativePrompt";
import { GenieSharedMedia } from "@/pages/agent-interfaces/live-agent/components/GenieAttachmentCard";
import { geniePagePath, isPageLive } from "@/pages/UserHub/GeniePages/geniePageStatus";
import { isSafeUrl } from "@/pages/agent-interfaces/live-agent/components/infoLinkIcons";
import { useStudioModeFlags } from "./hooks/useStudioModeFlags";
import { useStudioMobileViewport } from "./hooks/useStudioMobileViewport";
import { useDesktopStageFit } from "@/pages/agent-interfaces/live-agent/useDesktopStageFit";
import { useStudioPanels } from "./hooks/useStudioPanels";
import { useWelcomeGate } from "./hooks/useWelcomeGate";
import { useQrActivityLogging } from "./hooks/useQrActivityLogging";
import { useStudioImageAttachments } from "./hooks/useStudioImageAttachments";
import { useEmbedChrome } from "./hooks/useEmbedChrome";
import { useHumanHandoffOrchestration } from "./hooks/useHumanHandoffOrchestration";
import { useStudioConversationCore } from "./hooks/useStudioConversationCore";
import { StudioDefaultBranch } from "./components/StudioDefaultBranch";

function AgentStudioPage({
  agentPageData: propAgentData,
  portalMode = false,
  demoMode,
  initialQuestion,
}: AgentPageProps = {}) {
  // EARLY EMBED HANDOFF.
  //
  // The widget popup loads /live-agent/:url?embed=true inside an iframe. That
  // case has its own self-contained component (EmbedLiveAgentExperience) so
  // widget bugs can be debugged without the desktop/mobile/portal/demo
  // surface area in scope. The condition is computed here (via
  // useStudioModeFlags), but the actual `return` happens after every hook
  // below has run — a conditional return BEFORE hooks would change the hook
  // count/order between renders (React "Rendered more hooks than during the
  // previous render" if embedParam or isInIframe ever differs across a
  // re-render of the same mounted instance).
  const { isEmbedMode, isEarlyEmbedHandoff } = useStudioModeFlags(portalMode, demoMode);

  const { url_name } = useParams<{ url_name: string }>();
  const navigate = useNavigate();
  const { isInternalAdmin } = useRole();
  const { userProfile } = useAuth();
  const { showAfterConversationOnce } = useInstallAppBanner();
  const memoryConsentMutation = useCustomerMemoryConsent();

  const searchParams = new URLSearchParams(window.location.search);
  const { data: fetchedAgentResult, isLoading: isFetching } = useAgentPageByUrlNameFull(url_name);
  const [agent, setAgent] = useState<AgentPageProps["agentPageData"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [privateGenie, setPrivateGenie] = useState<{ genieName: string; passwordProtected: boolean } | null>(null);
  const [showInviteGate, setShowInviteGate] = useState(() => searchParams.get("type") === "invite");
  const isMobile = useStudioMobileViewport();
  const stageFit = useDesktopStageFit();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState<string>("");
  // Which entry channel is active — voice is the default; phone/email swap
  // the avatar/chat area for CallStage/EmailStage, matching the live-agent
  // page's own channel switcher. Deep-links via `?channel=`, written back
  // with history.replaceState so the Genie Page's doors can land a visitor
  // straight into a channel, same as LiveAgentPage.
  const channelParam = searchParams.get("channel");
  // `?ask=` (MAXX's suggested-question deep link) implies the same starting
  // point as `?channel=chat` — skip the voice-first default so the seeded
  // question effect below lands on the typed chat, not the start chooser.
  const askParam = parseAskParam(searchParams.get("ask"));
  const [channel, setChannel] = useState<"voice" | "chat" | "phone" | "email">(
    channelParam === "chat" || channelParam === "phone" || channelParam === "email"
      ? channelParam
      : askParam
        ? "chat"
        : "voice",
  );
  const channelRequests = useChannelRequests({ agentId: agent?.id, urlName: agent?.url_name ?? url_name });
  // Text starts and the voice transcript's Full text view action share the
  // desktop conversation canvas. This changes presentation only: the session,
  // audio preferences, messages and composer stay owned by this page.
  const [fullTextView, setFullTextView] = useState(false);
  const {
    showResources,
    setShowResources,
    showDetails,
    setShowDetails,
    showWrapUp,
    setShowWrapUp,
    mobileDrawerOpen,
    setMobileDrawerOpen,
  } = useStudioPanels();
  const { showWelcome, setShowWelcome, welcomeSeenForGenie, dismissWelcome } = useWelcomeGate(agent?.id);

  // Fetched lazily — only once the visitor opens the details/resources panel —
  // matching LiveAgentPage's ResourceLoungeContent, which needs the hub's
  // guides/downloads/help-centre link alongside agent.info_links.
  const { data: helpHubData } = usePublicHelpHub(url_name, showDetails);

  // Cheap, frontend-only "is voice struggling?" signal (see the hook) — feeds
  // both the persistent inline recovery card (hasVoiceTrouble, dismissible,
  // shown right under the avatar) and the full fallback sheet (showVoiceTrouble,
  // manual trigger only). No ML, no backend quality scoring. Matches
  // LiveAgentPage: the auto-offer opens the inline card, not the sheet.
  const [showVoiceTrouble, setShowVoiceTrouble] = useState(false);
  const [hasVoiceTrouble, setHasVoiceTrouble] = useState(false);
  const voiceTroubleSignals = useVoiceTroubleSignals();
  useEffect(() => {
    if (voiceTroubleSignals.autoOfferToken > 0) setHasVoiceTrouble(true);
  }, [voiceTroubleSignals.autoOfferToken]);
  // True from the moment a human handoff is confirmed until the human call
  // itself ends — suppresses the normal end-of-AI-conversation wrap-up so it
  // doesn't pop up while the Twilio call is starting/in progress.
  const handoffInProgressRef = useRef(false);
  // A configured redirect_url that fired while a handoff was in progress —
  // held until the human call ends instead of navigating away mid-call.
  const pendingRedirectUrlRef = useRef<string | null>(null);

  const canManageCustomerMemory =
    userProfile?.role === "consumer" && agent?.memory_enabled === true && !!agent?.id;

  // handleRedirect is referenced (as a plain identifier) inside callbacks
  // passed into useStudioConversationCore below, but as a `const`, it isn't
  // hoisted. It's declared here, above that hook call, so the closures it
  // captures are already-initialized bindings by the time they can run.
  const handleRedirect = (redirectUrl: string) => {
    console.log("Redirecting to:", redirectUrl);
    setRedirectMessage("Redirecting...");
    setIsRedirecting(true);

    // Show redirecting screen for a bit before redirecting
    setTimeout(() => {
      // Validate URL and redirect
      try {
        if (!isSafeUrl(redirectUrl)) throw new Error("Unsupported redirect protocol");
        const url = new URL(redirectUrl, window.location.origin);
        window.location.href = url.toString();
      } catch (error) {
        console.error("Invalid redirect URL:", redirectUrl);
        setIsRedirecting(false);
      }
    }, 200);
  };

  const updateCustomerMemoryConsent = async (enabled: boolean) => {
    if (!canManageCustomerMemory || !agent?.id) return;
    try {
      const result = await memoryConsentMutation.mutateAsync({ genieId: agent.id, enabled });
      setAgent((current) =>
        current
          ? {
              ...current,
              memoryConsent: result.memoryConsent,
              userMemory: enabled ? current.userMemory : null,
            }
          : current,
      );
      if (!enabled) voiceAgent.getService()?.setUserMemory(null);
    } catch (consentError) {
      console.error("Unable to update conversation memory preference:", consentError);
      toast.error("We couldn't update your memory preference. This conversation won't be remembered.");
    }
  };

  // useStudioImageAttachments needs the live conversationId and an
  // endConversation callback, both of which come from voiceAgent — but
  // voiceAgent itself is only created inside useStudioConversationCore below,
  // which in turn needs attachments/stageImages/etc as inputs. Broken by
  // threading the image-attachment pieces into the conversation-core hook as
  // refs (populated after useStudioImageAttachments runs, further down) —
  // they're only read inside handlers invoked after render completes, so the
  // ref indirection is invisible to callers.
  const attachmentsRef = useRef<
    Array<{
      id: string;
      file: File;
      previewUrl: string;
      status: "analyzing" | "ready" | "error";
      analysisText?: string;
      compressedBase64?: string;
    }>
  >([]);
  const stageImagesRef = useRef<
    (
      images: Array<{ id: string; file: File; analysisText?: string; compressedBase64?: string }>,
      conversationId: string | null,
    ) => void
  >(() => {});
  const removeAttachmentRef = useRef<(id: string) => void>(() => {});
  const setSubmittedImageCountRef = useRef<(updater: (count: number) => number) => void>(() => {});
  const endConversationAfterUploadsRef = useRef<() => Promise<void>>(async () => {});

  const {
    voiceAgent,
    expectedEndRef,
    lastConversationId,
    setLastConversationId,
    showTranscript,
    setShowTranscript,
    chatExpanded,
    setChatExpanded,
    messages,
    setMessages,
    conversationStartTime,
    setConversationStartTime,
    inlineMessage,
    setInlineMessage,
    isTextOnlyMode,
    setIsTextOnlyMode,
    isMicMuted,
    isAgentVoiceMuted,
    isPaused,
    startedFromText,
    setStartedFromText,
    isAllAudioMuted,
    shouldUseWrapUp,
    isTranscriptEnabled,
    handleInlineSend,
    deliverPendingAttachments,
    handleAvatarClick,
    handleStartVoiceFromWelcome,
    handleStartTextFromWelcome,
    handleToggleMicMute,
    handleToggleAgentVoice,
    handleTogglePause,
    handleToggleAllAudioMute,
    handleNoiseSwitchToText: handleNoiseSwitchToTextCore,
    handleEndTextChat,
    handleStartOver,
    getStatusMessage: getStatusMessageCore,
  } = useStudioConversationCore({
    agent,
    propAgentData,
    demoMode,
    isEmbedMode,
    isMobile,
    navigate,
    userProfile,
    memoryConsentMutation,
    canManageCustomerMemory,
    updateCustomerMemoryConsent,
    dismissWelcome,
    attachmentsRef,
    stageImagesRef,
    removeAttachmentRef,
    setSubmittedImageCountRef,
    endConversationAfterUploadsRef,
    voiceTroubleSignals,
    setShowVoiceTrouble,
    hasVoiceTrouble,
    setHasVoiceTrouble,
    showAfterConversationOnce,
    setShowWrapUp,
    handoffInProgressRef,
    pendingRedirectUrlRef,
    handleRedirect,
    setRedirectMessage,
    setIsRedirecting,
  });

  // Fires once per session the moment the trouble-call chip actually renders.
  useEffect(() => {
    if (voiceAgent.connected && hasVoiceTrouble && agent?.phone?.number) {
      trackEventOnce("live_agent_trouble_call_shown", "live_agent_trouble_call_shown", {
        agent_id: agent?.id ?? undefined,
      });
    }
  }, [voiceAgent.connected, hasVoiceTrouble, agent?.phone?.number, agent?.id]);

  const conversationId = voiceAgent.getConversationId();

  // Latched true once a live conversation transitions to disconnected —
  // triggers a final resources filter pass (see useFilteredResources). Reset
  // as soon as a new conversation connects.
  const [conversationEnded, setConversationEnded] = useState(false);
  const wasConnectedRef = useRef(false);
  useEffect(() => {
    if (voiceAgent.connected) {
      wasConnectedRef.current = true;
      setConversationEnded(false);
    } else if (wasConnectedRef.current) {
      wasConnectedRef.current = false;
      setConversationEnded(true);
    }
  }, [voiceAgent.connected]);
  const {
    attachments,
    addFiles,
    removeAttachment,
    updateAttachmentAnalysis,
    setSubmittedImageCount,
    stageImages,
    isSavingImages,
    pendingImageUploads,
    failedImageUploads,
    retryFailedImageUploads,
    endConversationAfterUploads,
    handleAddImage,
  } = useStudioImageAttachments(conversationId, userProfile?.id, voiceAgent.endConversation, expectedEndRef);

  attachmentsRef.current = attachments;
  stageImagesRef.current = stageImages;
  removeAttachmentRef.current = removeAttachment;
  setSubmittedImageCountRef.current = setSubmittedImageCount;
  endConversationAfterUploadsRef.current = endConversationAfterUploads;

  useEffect(() => {
    deliverPendingAttachments();
    // deliverPendingAttachments reads live values through refs; only these changes can make an image deliverable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, voiceAgent.connected]);

  const { isNoisy: showNoiseWarning, dismiss: dismissNoiseWarning } = useNoiseEnvironment({
    getService: voiceAgent.getService,
    enabled: voiceAgent.connected && !isTextOnlyMode && !startedFromText,
    isAgentSpeaking: voiceAgent.speaking,
  });

  // The noise-hint "switch to text" action needs to dismiss the noise sheet
  // (owned by useNoiseEnvironment, above, which itself depends on voiceAgent —
  // so it can't live inside useStudioConversationCore) before running the
  // hook's core switch-to-text flow.
  const handleNoiseSwitchToText = async () => {
    dismissNoiseWarning();
    await handleNoiseSwitchToTextCore();
  };

  const {
    voiceBridge,
    isVoiceBridgeEnabled,
    showHumanHandoffButton,
    showHandoffPrompt,
    setShowHandoffPrompt,
    handoffError,
    setHandoffError,
    handleConfirmHumanHandoff,
    handleHangupHumanHandoff,
  } = useHumanHandoffOrchestration({
    agent,
    voiceAgent,
    messageCount: messages.length,
    shouldUseWrapUp,
    setShowWrapUp,
    handleRedirect,
    handoffInProgressRef,
    pendingRedirectUrlRef,
  });

  // Status message is suppressed during a live human call since HumanCallBar
  // shows its own status right below the avatar — voiceBridge lives in
  // useHumanHandoffOrchestration, so the page wraps the conversation-core
  // hook's base getter with that check.
  const getStatusMessage = () =>
    isVoiceBridgeEnabled && voiceBridge.bridgeStatus !== "idle" ? "" : getStatusMessageCore();

  useEmbedChrome(isEmbedMode, url_name, voiceAgent.connected, agent?.elevenlabs_id);

  // Keep the URL's `?channel=` in sync so the current tab is a shareable deep
  // link (the Genie Page's doors land straight into a channel). Skipped in
  // the embed popup / demo card, where the URL isn't the visitor-facing one.
  useEffect(() => {
    if (isEmbedMode || demoMode) return;
    const url = new URL(window.location.href);
    const current = url.searchParams.get("channel");
    if (channel === "voice") {
      if (current === null) return;
      url.searchParams.delete("channel");
    } else {
      if (current === channel) return;
      url.searchParams.set("channel", channel);
    }
    window.history.replaceState({}, "", url.toString());
  }, [channel, isEmbedMode, demoMode]);

  // Any touch on the screen during a conversation reactivates wake lock strategies
  // This is critical for iOS Safari where strategies can be silently killed
  const handleScreenTouch = useCallback(() => {
    if (voiceAgent.connected && voiceAgent.wakeLock?.reactivateAll) {
      voiceAgent.wakeLock.reactivateAll();
    }
  }, [voiceAgent.connected, voiceAgent.wakeLock]);

  // When a visitor leaves their email (via the auto-pill or the "Get a summary"
  // chip), quietly let the Genie know so it doesn't ask for it again.
  const handleContactCaptured = useCallback(
    (email: string) => {
      voiceAgent.sendContextualUpdate(
        `[SYSTEM CONTEXT - DO NOT READ ALOUD]\nThe user just provided their email: ${email}`,
      );
    },
    [voiceAgent],
  );

  // Cleanup is handled by useVoiceAgentConversation hook

  const canEmailTeam =
    !isEmbedMode && !demoMode && !!agent?.url_name && (agent.is_public === true || agent.is_marketplace_genie === true);
  const followUp = useConversationFollowUp({
    agentId: agent?.id,
    initialEmail: userProfile?.email,
    conversationId: conversationId || lastConversationId,
    sessionKey: conversationId || lastConversationId,
    messages,
    enabled: canEmailTeam,
    pageUrl: `${window.location.origin}/studio/${agent?.url_name ?? url_name}`,
    askQuestion: channelRequests.askQuestion,
    onCaptured: handleContactCaptured,
  });
  const emailSummaryAction =
    followUp.available && messages.some((message) => !message.isUser && message.message.trim());

  // The genie's own branding first; the old page copy only as a fallback.
  const agentLogo = agent?.branding?.logoUrl || agent?.page?.genie_image_url;

  useEffect(() => {
    if (!agentLogo) return;
    const link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    const prev = link?.href;
    if (link) link.href = agentLogo;
    return () => {
      if (link && prev) link.href = prev;
    };
  }, [agentLogo]);

  const rawResourcesConfig = (agent?.page?.info_links as InfoLinksConfig | null) ?? null;
  const inChatMedia = hasInChatMedia(agent?.settings as AgentConfig | undefined);
  const resourcesConfig = useFilteredResources(
    conversationId,
    rawResourcesConfig,
    voiceAgent.connected,
    showResources,
    conversationEnded,
    messages,
    { inChatMedia },
  );

  // A genie that puts links in the chat itself has no rail: the cards are the
  // same links, shown better. The Resources sheet stays.
  //
  // QR-scan activity logging + resources rail item list/surfaced-highlight/
  // tracking. Hooks live above the early returns below, unconditionally, and
  // before the in-conversation capture hook, which uses the surfaced-resource
  // id as one of its arm signals.
  const {
    flushPendingQrLog,
    resetQrLog,
    railItems,
    surfacedResourceId,
    handleRailSelect,
    handleRailImpression,
  } = useQrActivityLogging(agent, showInviteGate, messages, inChatMedia ? null : resourcesConfig);
  // With the chat on screen and no gutter for the rail, the suggested
  // resources sit in the chat itself as cards.
  const chatMessages = useSuggestedResourceMessages(messages, railItems, voiceAgent.connected && !demoMode);
  const surfacedResourceItem = useMemo(
    () => railItems.find((item) => item.id === surfacedResourceId) ?? null,
    [railItems, surfacedResourceId],
  );

  // In-conversation capture moment — arms at the first genuine value moment
  // (a surfaced resource, or the Genie's first substantive answer), with
  // turn-count / elapsed-time fallbacks, and renders inline in whichever
  // transcript surface is active (mobile drawer, desktop overlay, or the
  // text-start canvas).
  const inConversationCapture = useInConversationCapture({
    connected: voiceAgent.connected,
    messages,
    conversationStartTime,
    agentId: agent?.id,
    agentUserId: agent?.user_id,
    surfacedResource: surfacedResourceItem,
    agentBusy: voiceAgent.speaking || voiceAgent.thinking || voiceAgent.processing || voiceAgent.searching || voiceAgent.retrieving,
    onCaptured: handleContactCaptured,
  });

  // A genie counting chat leads as ad conversions carries requestContactDetails;
  // its call opens this same moment as a contact ask.
  const leadConversions = agent?.branding?.embedStyle?.leadConversions;
  useEffect(() => {
    setLeadConversionSettings(leadConversions);
  }, [leadConversions]);

  const { requestContactDetails } = inConversationCapture;
  const { onContactDetailsRequested } = voiceAgent;
  useEffect(() => {
    onContactDetailsRequested(requestContactDetails);
    return () => onContactDetailsRequested(null);
  }, [onContactDetailsRequested, requestContactDetails]);

  const latestShare = useLatestAttachments(messages);
  const mediaPending = useMediaLookupPending();
  const hasFoundMedia = useFoundMedia().length > 0;

  // Seeded first question (Genie Pages). This must stay above every render
  // gate so loading the agent cannot change the component's hook order.
  const seededQuestionRef = useRef(false);

  // Reset all per-genie conversation/panel state when navigating to a
  // different url_name without a component remount (the /studio/:url_name
  // route reuses this instance across genies, same as LiveAgentPage). Without
  // this, stale messages/transcript/wrap-up/welcome state from the previous
  // genie would leak into the next one.
  useEffect(() => {
    if (propAgentData) return;
    setAgent(null);
    setError(null);
    setPrivateGenie(null);
    setMessages([]);
    setShowTranscript(false);
    setShowDetails(false);
    setFullTextView(false);
    setShowWrapUp(false);
    setShowWelcome(true);
    setShowVoiceTrouble(false);
    setLastConversationId(null);
    setSubmittedImageCount((count) => (count === 0 ? count : 0));
    seededQuestionRef.current = false;
    resetQrLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url_name, propAgentData]);

  // Agent loading effect - use prop data if available, otherwise use fetched data.
  // Must stay after the reset effect above: with the genie already cached both
  // run in the same pass, and the reset would otherwise wipe the genie just set.
  useEffect(() => {
    if (propAgentData) {
      setAgent(propAgentData);
      return;
    }

    if (isFetching) return;

    if (!fetchedAgentResult) {
      setError("Agent not found");
      return;
    }

    if (fetchedAgentResult.status === "private") {
      setPrivateGenie({
        genieName: fetchedAgentResult.genieName,
        passwordProtected: fetchedAgentResult.passwordProtected,
      });
      return;
    }

    if (fetchedAgentResult.status === "ok") {
      setPrivateGenie(null);
      setError(null);
      setAgent(fetchedAgentResult.agent);
    } else {
      setError("Agent not found");
    }
  }, [propAgentData, fetchedAgentResult, isFetching]);

  useEffect(() => {
    const question = (askParam ?? initialQuestion)?.trim();
    if (!question || seededQuestionRef.current || !agent) return;
    if (!voiceAgent.getService()) return;

    const resolvedUrlName = agent.url_name ?? url_name ?? "";
    // `ask` survives a plain reload of the same URL (unlike initialQuestion,
    // which an in-page caller passes fresh each time) — guard with
    // sessionStorage so reloading the same MAXX link doesn't ask again into a
    // brand-new conversation.
    if (askParam && hasAskAlreadyBeenSent(resolvedUrlName, askParam)) {
      seededQuestionRef.current = true;
      return;
    }

    seededQuestionRef.current = true;
    if (askParam) markAskAsSent(resolvedUrlName, askParam);
    setShowWelcome(false);
    void handleInlineSend(question);
    // handleInlineSend is recreated every render; the ref guard is what makes
    // this run once, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent, askParam, initialQuestion, voiceAgent, url_name]);

  if (isEarlyEmbedHandoff) {
    return <EmbedLiveAgentExperience agentPageData={propAgentData} />;
  }

  if (!agent && isFetching && !propAgentData) {
    return <BrandSpinner label="Awakening your Genie…" />;
  }
  if (privateGenie) {
    return (
      <GenieAccessRestrictedPage
        urlName={url_name ?? ""}
        genieName={privateGenie.genieName}
        passwordProtected={privateGenie.passwordProtected}
      />
    );
  }
  if (agent && showInviteGate) {
    return (
      <GenieInvitePage
        urlName={url_name ?? ""}
        genieName={agent.name ?? ""}
        agentId={agent.id ?? ""}
        branding={agent.branding}
        userEmail={userProfile?.email}
        onSkip={() => {
          flushPendingQrLog();
          setShowInviteGate(false);
        }}
        onSuccess={(consumerId) => {
          flushPendingQrLog(consumerId);
          setShowInviteGate(false);
        }}
      />
    );
  }
  if (error || !agent) {
    const isInactive = error?.includes?.("Genie Not Available") ?? false;
    return <GenieNotFound isInactive={isInactive} />;
  }

  if (agent.type === "listener") {
    return <ListenerExperience agent={agent} />;
  }

  const brand = agent?.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  // The tenant's own contact number, when this genie has one provisioned.
  // Generic lookup only — no HG-specific or hardcoded number, ever.
  const phoneNumber = agent?.phone?.number || null;
  const mailboxAddress = agent?.mailbox?.status === "provisioned" ? agent.mailbox.email_address : null;
  const inlineCaptureNode = inConversationCapture.visible ? (
    <InlineCaptureMoment
      brandColor={brand}
      secondaryColor={agent?.branding?.secondaryColor}
      agentName={agent?.name}
      resourceLabel={inConversationCapture.resourceLabel}
      contactRequest={inConversationCapture.contactRequested}
      values={inConversationCapture.capture.values}
      onChange={inConversationCapture.capture.onChange}
      onSubmit={inConversationCapture.capture.submit}
      isSubmitting={inConversationCapture.capture.isSubmitting}
      submitted={inConversationCapture.capture.submitted}
      error={inConversationCapture.capture.error}
      onDismiss={inConversationCapture.dismiss}
      alreadyCapturedEmail={
        inConversationCapture.capture.alreadyCaptured && !inConversationCapture.capture.editing
          ? inConversationCapture.capture.capturedEmail
          : null
      }
      onChangeEmail={inConversationCapture.capture.startEdit}
    />
  ) : null;
  if (isRedirecting) {
    return <RedirectingScreen brandColor={brand} message={redirectMessage || "Redirecting..."} />;
  }

  // Popup Appearance fallbacks. Full Page Appearance styles this page, embedded
  // or not (resolved in StudioDefaultBranch); embedded, these popup values fill
  // in whatever the full page never set. Null on the regular page, which keeps
  // its own defaults.
  const embedCtaStyle = isEmbedMode ? agent?.branding?.embedStyle?.ctaButton : null;
  const embedCtaShape = isEmbedMode ? agent?.branding?.embedStyle?.buttonStyle : undefined;
  const embedActionButtonStyle = isEmbedMode ? agent?.branding?.embedStyle?.actionButtons : null;
  const embedAvatarImageUrl = isEmbedMode ? agent?.branding?.embedStyle?.avatarImageUrl : null;
  const embedAvatarShowLogoOverlay = isEmbedMode ? agent?.branding?.embedStyle?.avatarShowLogoOverlay : false;
  const embedAvatarSizeScale = isEmbedMode ? agent?.branding?.embedStyle?.avatarSizeScale : undefined;

  const hasResources = !!(
    rawResourcesConfig &&
    ((rawResourcesConfig.documents?.length ?? 0) > 0 || (rawResourcesConfig.links?.length ?? 0) > 0)
  ) || hasFoundMedia;

  // The Genie Page is this genie's hub — guides, FAQs and every way in. Offer
  // it from the details panel whenever it's live, except in portal mode, where
  // the visitor is already inside that very page.
  const helpPageUrl = !portalMode && agent && isPageLive(agent) ? geniePagePath(agent) : null;

  // The Details tab + poof view are a mid-conversation affordance only. Before
  // a conversation, More and the support-page link cover alternate channels,
  // so the floating tab stays hidden until a call is actually live. Excluded
  // also from the embed popup, the marketing demo card, and behind an open
  // mobile drawer.
  const showDetailsAffordance =
    voiceAgent.connected &&
    !isEmbedMode &&
    !demoMode &&
    !(isMobile && mobileDrawerOpen);

  // The "Having trouble?" fallback only makes sense on the live voice stage —
  // once a visitor is already in text (chat panel open) they have already
  // self-served the fallback this offers.
  const showVoiceTroubleTrigger =
    voiceAgent.connected &&
    !isTextOnlyMode &&
    !showTranscript &&
    !isPaused &&
    !showWrapUp &&
    !showDetails &&
    !isEmbedMode &&
    !demoMode &&
    !(isMobile && mobileDrawerOpen);

  // The transcript already owns the right column, so the rail takes the left
  // gutter. Both gutters stay reserved even when the transcript is closed, so
  // opening the chat never shifts the orb or moves the rail. Framed or not,
  // the gutter only shows while the viewport has room for it (see
  // useDesktopStageFit); otherwise the rail moves into the genie or chat column.
  const railInGutter = railItems.length > 0 && !demoMode;

  // Desktop full-page text chat gets its own dedicated surface (the
  // conversation canvas) instead of the widget's chat card stretched into an
  // 860px column. Embed/demo/mobile keep the existing layouts untouched.
  const conversationCanvasActive =
    !isMobile &&
    !isEmbedMode &&
    !demoMode &&
    (channel === "chat" ||
      ((startedFromText || fullTextView) &&
        showTranscript && (isTranscriptEnabled() || isTextOnlyMode)));

  const agentName = agent?.name;
  const agentDescription = agent?.description;
  const pageTitle = agentName ? `${agentName}` : "Live Agent";
  const shareUrl = `${window.location.origin}/studio/${url_name}`;

  const helmet = (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={agentDescription || ""} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={agentDescription || ""} />
      <meta property="og:url" content={shareUrl} />
      <meta property="og:type" content="website" />
      {agentLogo && <meta property="og:image" content={agentLogo} />}
    </Helmet>
  );

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/studio/${url_name}`;
      const shareData = {
        title: agentName ? `${agentName} — Live Agent` : "Live Agent",
        text: agentDescription || "Talk to my Genie live:",
        url: shareUrl,
      } as ShareData;

      // Prefer native share when available
      if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch (err) {
      try {
        const fallbackUrl = `${window.location.origin}/studio/${url_name}`;
        await navigator.clipboard.writeText(fallbackUrl);
        toast.success("Link copied to clipboard");
      } catch (_) {
        toast.error("Unable to share. Please copy the URL from the address bar.");
      }
    }
  };

  // Persistent inline "having trouble connecting?" recovery card — offers a
  // call/text fallback after repeated quick, reply-less sessions or a failed
  // start attempt. Distinct from the manually-triggered VoiceTroubleSheet.
  const voiceTroubleCard =
    hasVoiceTrouble && !isEmbedMode && !demoMode ? (
      <div className="mt-4 w-full max-w-md px-3">
        <ConversationAlternativePrompt
          kind="voice"
          phoneNumber={phoneNumber}
          onDismiss={() => setHasVoiceTrouble(false)}
          onSwitchToText={() => void handleNoiseSwitchToText()}
          disabled={voiceAgent.isConnecting}
          onCall={() => {
            if (voiceAgent.connected) void endConversationAfterUploads();
          }}
        />
      </div>
    ) : null;

  // The transcript already shows shared links/pictures inline, so this only
  // covers the voice stage while the chat is out of view. The desktop layout
  // puts it in the closed transcript's column. Mobile and narrow desktop
  // screens or frames have no side column, so there it goes under the action
  // row: through the mobile layout's slot on mobile, through AgentCore's
  // elsewhere.
  const sharedMedia =
    voiceAgent.connected &&
    !showTranscript &&
    !conversationCanvasActive &&
    !(isMobile && mobileDrawerOpen) &&
    (latestShare.attachments.length > 0 || mediaPending) ? (
      <GenieSharedMedia
        attachments={latestShare.attachments}
        brandColor={brand}
        agentName={agent?.name}
        agentId={agent?.id}
        onDismiss={latestShare.dismiss}
        pending={mediaPending}
      />
    ) : null;
  const sharedMediaInColumn = !isMobile && stageFit.fullGenieBesideSideColumn;
  const sharedMediaCard =
    !isMobile && !sharedMediaInColumn && sharedMedia ? (
      <div className="mt-4 flex w-full max-w-md justify-center px-3">{sharedMedia}</div>
    ) : null;

  // Shared props for AgentCore — the avatar/controls/status/action-row/handoff
  // cluster rendered (with per-surface overrides) on the mobile layout and the
  // desktop layout.
  const agentCoreBaseProps = {
    brand,
    agent,
    isEmbedMode,
    isMobile,
    voiceAgent: {
      speaking: voiceAgent.speaking,
      listening: voiceAgent.listening,
      thinking: voiceAgent.thinking,
      processing: voiceAgent.processing,
      searching: voiceAgent.searching,
      retrieving: voiceAgent.retrieving,
      isConnecting: voiceAgent.isConnecting,
      connected: voiceAgent.connected,
    },
    onAvatarClick: () => void handleAvatarClick(),
    isVoiceBridgeEnabled,
    voiceBridge: {
      bridgeStatus: voiceBridge.bridgeStatus,
      muted: voiceBridge.muted,
      toggleMute: voiceBridge.toggleMute,
    },
    showHumanHandoffButton,
    onOpenHandoffPrompt: () => setShowHandoffPrompt(true),
    onHangupHandoff: handleHangupHumanHandoff,
    embedAvatarImageUrl,
    embedAvatarShowLogoOverlay,
    embedAvatarSizeScale,
    embedCtaShape,
    embedCtaStyle,
    isTranscriptEnabled: isTranscriptEnabled(),
    isMicMuted,
    onToggleMicMute: () => void handleToggleMicMute(),
    isAgentVoiceMuted,
    onToggleAgentVoice: () => void handleToggleAgentVoice(),
    isPaused,
    onTogglePause: () => void handleTogglePause(),
    onAddImage: handleAddImage,
    onSummaryCaptured: handleContactCaptured,
    embedActionButtonStyle,
    onStartText: handleStartTextFromWelcome,
    phoneNumber,
    onCallGenie: () => setChannel("phone"),
    mailboxAddress,
    onEmailGenie: followUp.available ? followUp.openSheet : mailboxAddress ? () => setChannel("email") : undefined,
    voiceTroubleCard,
    sharedMediaCard,
  };

  const pauseOverlayProps = {
    isPaused,
    onResume: () => void handleTogglePause(),
    onEndConversation: () => void handleEndTextChat(),
  };

  return (
    <StudioDefaultBranch
      agent={agent}
      brand={brand}
      isEmbedMode={isEmbedMode}
      demoMode={!!demoMode}
      portalMode={portalMode}
      isMobile={isMobile}
      isInternalAdmin={isInternalAdmin()}
      conversationCanvasActive={conversationCanvasActive}
      sharedMediaColumn={sharedMediaInColumn ? sharedMedia : null}
      helmet={helmet}
      onScreenTouch={handleScreenTouch}
      agentLogo={agentLogo}
      studioUrl={`${window.location.origin}/studio/${url_name}`}
      headerActionsProps={{
        onBack: () => navigate("/genie-hub/my-genies"),
        onShare: handleShare,
      }}
      agentCoreBaseProps={agentCoreBaseProps}
      getStatusMessage={getStatusMessage}
      layoutState={{
        messages,
        chatMessages,
        showTranscript,
        setShowTranscript,
        startedFromText,
        isTextOnlyMode,
        isTranscriptEnabled,
        chatExpanded,
        setChatExpanded,
        inlineMessage,
        setInlineMessage,
        onSendChat: () => void handleInlineSend(),
        isMicMuted,
        onToggleMicMute: () => void handleToggleMicMute(),
        isAgentVoiceMuted,
        onToggleAgentVoice: () => void handleToggleAgentVoice(),
        isPaused,
        onTogglePause: () => void handleTogglePause(),
        isAllAudioMuted,
        onToggleAllAudioMute: () => void handleToggleAllAudioMute(),
        onEndConversation: () => void endConversationAfterUploads(),
        onEndChat: () => void handleEndTextChat(),
        inlineCaptureNode,
      }}
      resourcesRail={{
        railItems,
        resourcesConfig,
        surfacedResourceId,
        onOpenAll: () => setShowResources(true),
        onSelect: (id: string) => {
          const item = railItems.find((railItem) => railItem.id === id);
          if (item) handleRailSelect(item);
        },
        onImpression: (id: string) => {
          const item = railItems.find((railItem) => railItem.id === id);
          if (item) handleRailImpression(item);
        },
        railInGutter,
      }}
      attachmentsState={{
        attachments,
        onRemoveAttachment: removeAttachment,
        onUpdateAttachmentAnalysis: updateAttachmentAnalysis,
        pendingImageUploads,
        failedImageUploads,
        isSavingImages,
        onRetryImageUploads: retryFailedImageUploads,
        onAddFiles: addFiles,
      }}
      mobileLayoutProps={{
        forceTranscriptOpen: showTranscript,
        suppressTranscript: showWrapUp && !voiceAgent.connected && !voiceAgent.isConnecting,
        sharedMedia: isMobile ? sharedMedia : null,
        onDrawerOpenChange: (open) => {
          setMobileDrawerOpen(open);
          setShowTranscript(open);
        },
        mobileDrawerOpen,
      }}
      modalsProps={{
        lastConversationId,
        tapReminder: {
          showReminder: voiceAgent.tapReminder.showReminder,
          dismissReminder: voiceAgent.tapReminder.dismissReminder,
        },
        showNoiseWarning,
        dismissNoiseWarning,
        onNoiseSwitchToText: () => void handleNoiseSwitchToText(),
        showResources,
        onCloseResources: () => setShowResources(false),
        showVoiceTroubleTrigger,
        setShowVoiceTrouble,
        showVoiceTrouble,
        phoneNumber,
        onContactCaptured: handleContactCaptured,
      }}
      detailsProps={{
        showDetails,
        setShowDetails,
        showDetailsAffordance,
        hasResources,
        helpPageUrl,
        helpHub: helpHubData,
        canManageCustomerMemory,
        memoryUpdating: memoryConsentMutation.isPending,
        onMemoryConsentChange: (enabled) => void updateCustomerMemoryConsent(enabled),
      }}
      wrapUpProps={{
        showWrapUp,
        setShowWrapUp,
        onStartOver: handleStartOver,
        onSwitchToText: () => {
          setShowWrapUp(false);
          void handleStartTextFromWelcome();
        },
      }}
      handoffProps={{
        isVoiceBridgeEnabled,
        showHandoffPrompt,
        setShowHandoffPrompt,
        setHandoffError,
        handoffError,
        bridgeStatus: voiceBridge.bridgeStatus,
        onConfirmHandoff: () => void handleConfirmHumanHandoff(),
      }}
      welcomeProps={{
        showWelcome,
        welcomeSeenForGenie,
        showInviteGate,
        canManageCustomerMemory,
        onStartVoice: handleStartVoiceFromWelcome,
        onStartText: handleStartTextFromWelcome,
      }}
      pauseOverlayProps={pauseOverlayProps}
      channelProps={{
        channel,
        setChannel,
        phoneNumber,
        mailboxAddress,
        onCallGenie: () => setChannel("phone"),
        onEmailGenie: () => setChannel("email"),
        callbackAvailable: agent?.outbound_connected === true,
        onRequestCallback: channelRequests.requestCallback,
        onAskQuestion: channelRequests.askQuestion,
        channelPageUrl: `${window.location.origin}/studio/${url_name}`,
        helpPageUrl,
      }}
      followUpProps={{
        fullTextView,
        setFullTextView,
        emailSummaryAction,
        emailSummaryPending: followUp.isPending,
        emailSummaryRequested: followUp.purpose === "summary" && followUp.isSuccess,
        onEmailSummary: emailSummaryAction ? followUp.openSummarySheet : undefined,
        onEmailFollowUp: followUp.available ? followUp.openSheet : mailboxAddress ? () => setChannel("email") : undefined,
        open: followUp.open,
        onClose: followUp.close,
        form: followUp.form,
        purpose: followUp.purpose,
        contextTruncated: followUp.contextTruncated,
        onSubmit: followUp.submit,
        isPending: followUp.isPending,
        isSuccess: followUp.isSuccess,
        error: followUp.error,
      }}
    />
  );
}

const Centered = ({ children }) => (
  <div
    className="min-h-dvh flex items-center justify-center bg-[#F9FAFC] text-xl font-semibold"
    style={{ color: DEFAULT_BRANDING.primaryColor }}
  >
    {children}
  </div>
);

export default AgentStudioPage;
